import {useCallback} from "react";
import {getMessages, stopMaestroRequest} from "~/src/api";
import {uuidv7} from "uuidv7";
import {
  clearMessageActivity,
  removeActivityOnlyMessage,
} from "~/src/pages/RexAITrustedAdvisor/Context/streamMessageHelpers";
import {reconcileSessionMessages} from "~/src/pages/RexAITrustedAdvisor/Context/sessionMessageHelpers";
import {some} from "underscore";

// The first outcome read is immediate; twenty intervals allow ten seconds for settlement.
const OUTCOME_POLL_ATTEMPTS = 21;
const STOPPED_SESSION_POLL_ATTEMPTS = 10;
const OUTCOME_POLL_INTERVAL_MS = 500;

const useStoppedRequestRecovery = ({
  apiClient,
  email,
  sessionId,
  formatMessages,
  fetchSessions,
  restoreSessionState,
  restoreUnconfirmedInput,
  clearRecoveredInput,
  refs: {
    activeInputRef,
    currentAssistantMessageId,
    fullResponseRef,
    inputRef,
    lastChunkIndexRef,
    mountedRef,
    openSessionIdRef,
    sessionIdRef,
    stoppedRef,
    streamAbortControllerRef,
    viewVersionRef,
  },
  actions: {
    setCurrentRequest,
    setError,
    setIsStopping,
    setIsThinking,
    setMessages,
    setSessionId,
    setSkipSubscription,
  },
}) => {
  const refreshRequestOutcome = useCallback(
    async (
      targetSessionId,
      sourceMessageId,
      expectedViewVersion = viewVersionRef.current,
      {preserveRetryGuidance = false, isResume = false} = {},
    ) => {
      if (!targetSessionId || !email) {
        return;
      }
      const isCurrentView = () =>
        mountedRef.current &&
        viewVersionRef.current === expectedViewVersion &&
        (!activeInputRef.current?.messageId ||
          activeInputRef.current.messageId === sourceMessageId) &&
          (!openSessionIdRef.current || openSessionIdRef.current === targetSessionId);
      let confirmationFailureLogged = false;
      for (let attempt = 0; attempt < OUTCOME_POLL_ATTEMPTS; attempt++) {
        if (attempt) {
          await new Promise((resolve) => setTimeout(resolve, OUTCOME_POLL_INTERVAL_MS));
        }
        if (!isCurrentView()) {
          return;
        }
        try {
          const data = await getMessages(apiClient, {
            sessionId: targetSessionId,
            email,
          });
          if (!isCurrentView()) {
            return;
          }
          if (!data) {
            throw new Error("Empty stopped-request outcome response");
          }
          const hasOutcome = some(
            data.messages,
            (message) =>
              // A read-time Stop projection is not the streaming worker's saved reply.
              // Keep the reserved-ID check for backend versions without the explicit flag.
              message.role === "assistant" &&
              !message.stopProjection &&
              message.messageId !== `STOP#${sourceMessageId}` &&
              (isResume ?
                message.resumeRequestId === sourceMessageId :
                message.parentMessageId === sourceMessageId),
          );
          let sessionState;
          try {
            sessionState = await restoreSessionState(targetSessionId, data, isCurrentView);
          } catch (error) {
            if (!isCurrentView()) {
              return;
            }
            if (!confirmationFailureLogged) {
              console.error("Failed to restore pending confirmation during recovery:", error);
              confirmationFailureLogged = true;
            }
          }
          if (!isCurrentView()) {
            return;
          }
          const hasMatchingPending =
            !isResume &&
            sessionState?.hasPending &&
            sessionState.pendingSourceMessageId === sourceMessageId;
          const hasRecoverableOutcome = hasOutcome || hasMatchingPending;
          if (!hasRecoverableOutcome && attempt < OUTCOME_POLL_ATTEMPTS - 1) {
            continue;
          }
          if (!hasRecoverableOutcome && preserveRetryGuidance) {
            return;
          }
          openSessionIdRef.current = targetSessionId;
          setSessionId(targetSessionId);
          if (hasRecoverableOutcome) {
            setMessages((previous) => reconcileSessionMessages(previous, formatMessages(data)));
            clearRecoveredInput(sourceMessageId);
            setError("");
          } else {
            setError("Result still pending. Check this conversation later.");
          }
          // Sidebar failure does not invalidate a saved reply or restart outcome polling.
          try {
            await fetchSessions();
          } catch (error) {
            if (!isCurrentView()) {
              return;
            }
            console.error("Failed to refresh sessions after request recovery:", error);
          }
          return;
        } catch (error) {
          if (!isCurrentView()) {
            return;
          }
          if (attempt === OUTCOME_POLL_ATTEMPTS - 1) {
            console.error("Failed to refresh stopped request:", error);
          }
        }
      }
      if (isCurrentView() && !preserveRetryGuidance) {
        setError("Could not verify the saved result. Reopen this conversation to check it.");
      }
    },
    [
      apiClient,
      clearRecoveredInput,
      email,
      fetchSessions,
      formatMessages,
      restoreSessionState,
      setError,
      setMessages,
      setSessionId,
    ],
  );

  const recoverStoppedSession = useCallback(
    async (
      messageId,
      partialContent,
      expectedViewVersion,
      {preserveRetryGuidance = false} = {},
    ) => {
      for (let attempt = 0; attempt < STOPPED_SESSION_POLL_ATTEMPTS; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, OUTCOME_POLL_INTERVAL_MS));
        if (!mountedRef.current) {
          return;
        }
        if (viewVersionRef.current !== expectedViewVersion) {
          return;
        }
        if (activeInputRef.current?.messageId) {
          return;
        }
        try {
          const status = await stopMaestroRequest(apiClient, {messageId, partialContent});
          if (!mountedRef.current || viewVersionRef.current !== expectedViewVersion) {
            return;
          }
          if (activeInputRef.current?.messageId) {
            return;
          }
          if (status?.sessionId) {
            await refreshRequestOutcome(status.sessionId, messageId, expectedViewVersion, {
              preserveRetryGuidance,
            });
            return;
          }
        } catch (error) {
          if (error?.status && error.status < 500 && error.status !== 429) {
            console.error("Failed to locate stopped request:", error);
            break;
          }
          if (attempt === STOPPED_SESSION_POLL_ATTEMPTS - 1) {
            console.error("Failed to locate stopped request:", error);
          }
        }
      }
      if (!mountedRef.current || viewVersionRef.current !== expectedViewVersion) {
        return;
      }
      if (activeInputRef.current?.messageId) {
        return;
      }
      if (preserveRetryGuidance) {
        return;
      }
      setError("Stop saved, but this conversation is not available yet. Reopen it to check.");
      await fetchSessions();
    },
    [apiClient, fetchSessions, refreshRequestOutcome, setError],
  );

  const stopGeneration = async ({departure} = {}) => {
    if (!email || (departure && (!mountedRef.current ||
      activeInputRef.current?.messageId !== departure.messageId))) {
      return;
    }

    const viewVersion = viewVersionRef.current;
    const messageIdToAbort = currentAssistantMessageId.current;
    const partialContent = fullResponseRef.current;
    const currentSessionId = sessionId || sessionIdRef.current;
    const activeInput = activeInputRef.current;
    const isResume = !!activeInput?.agentTaskId;
    const stoppedBeforeAdmission = activeInput?.prompt && !activeInput.accepted;
    const streamAbortController = streamAbortControllerRef.current;

    stoppedRef.current = true;
    setIsStopping(true);
    setIsThinking(false);

    let stopResult;
    // Approval resumes have their own durable cancellation identity, never the original input.
    try {
      if (departure) {
        // The returning page resumes settlement of its original Stop, not model execution.
        stopResult = await departure.stopPromise;
      } else if (activeInput?.messageId) {
        stopResult = await stopMaestroRequest(apiClient, {
          messageId: activeInput.messageId,
          ...(currentSessionId && {sessionId: currentSessionId}),
          partialContent: isResume ? "" : partialContent,
          ...(isResume && {
            agentTaskId: activeInput.agentTaskId,
            ...(activeInput.confirmationId && {confirmationId: activeInput.confirmationId}),
          }),
        });
      }
    } catch (err) {
      console.error("Failed to record Maestro Stop:", err);
    }

    streamAbortController?.abort();
    if (!mountedRef.current || viewVersionRef.current !== viewVersion) {
      if (mountedRef.current) {
        setIsStopping(false);
      }
      return;
    }
    setSkipSubscription(true);
    stoppedRef.current = false;
    fullResponseRef.current = "";
    currentAssistantMessageId.current = null;
    lastChunkIndexRef.current = -1;

    const outcomeSessionId = stopResult?.sessionId || currentSessionId;
    if (stopResult?.status === "already_settled") {
      // The server may have completed normally. Clear progress without claiming cancellation.
      removeActivityOnlyMessage(setMessages, messageIdToAbort);
      clearMessageActivity(setMessages, messageIdToAbort);
      setCurrentRequest(null);
      setIsStopping(false);
      inputRef.current?.focus();
      if (outcomeSessionId) {
        await refreshRequestOutcome(outcomeSessionId, activeInput.messageId, viewVersion, {
          isResume,
        });
      } else {
        setError("Could not locate the saved result. Reopen this conversation to check it.");
      }
      return;
    }

    const stopFailed = activeInput?.messageId && !stopResult?.success;
    const stoppedMessage = stopFailed ?
      "_Live response stopped; server Stop could not be confirmed._" :
      "_⚠️ This task stopped before it could complete._";
    if (!stoppedBeforeAdmission && messageIdToAbort) {
      setMessages((prev) => {
        const newMessages = [...prev];
        const lastMsg = newMessages[newMessages.length - 1];
        if (lastMsg?.messageId === messageIdToAbort) {
          newMessages[newMessages.length - 1] = {
            ...lastMsg,
            locallyStopped: true,
            ...(isResume ?
              {resumeRequestId: activeInput?.messageId} :
              {parentMessageId: activeInput?.messageId}),
            ...(lastMsg.text && !isResume ?
              {aborted: true} :
              {text: stoppedMessage, aborted: false}),
            ...(isResume && {thinking: undefined, citations: undefined}),
            activity: undefined,
          };
        }
        return newMessages;
      });
    } else if (!stoppedBeforeAdmission) {
      setMessages((prev) => [
        ...prev,
        {
          messageId: uuidv7(),
          text: stoppedMessage,
          sender: "assistant",
          createdAt: Date.now(),
          ephemeral: true,
          locallyStopped: true,
          ...(isResume ?
            {resumeRequestId: activeInput?.messageId} :
            {parentMessageId: activeInput?.messageId}),
        },
      ]);
    }

    if (stopFailed) {
      setError("Could not confirm Stop. Reopen this conversation to check the result.");
      if (stoppedBeforeAdmission) {
        restoreUnconfirmedInput(activeInput.messageId);
      }
      setCurrentRequest(null);
      setIsStopping(false);
      inputRef.current?.focus();
      return;
    }

    if (stopResult?.sessionId) {
      openSessionIdRef.current = stopResult.sessionId;
      setSessionId(stopResult.sessionId);
    }

    if (stoppedBeforeAdmission) {
      restoreUnconfirmedInput(activeInput.messageId);
      setError("Stopped. Send again to check what was saved.");
    }

    setCurrentRequest(null);

    if (activeInput?.messageId && outcomeSessionId) {
      refreshRequestOutcome(outcomeSessionId, activeInput.messageId, viewVersion, {
        preserveRetryGuidance: !!stoppedBeforeAdmission,
        isResume,
      }).catch((error) => {
        console.error("Failed to refresh stopped request:", error);
      });
    } else if (activeInput?.messageId) {
      recoverStoppedSession(activeInput.messageId, partialContent, viewVersion, {
        preserveRetryGuidance: !!stoppedBeforeAdmission,
      }).catch((error) => {
        console.error("Failed to recover stopped session:", error);
      });
    } else if (currentSessionId) {
      setTimeout(fetchSessions, 3000);
    }

    setTimeout(() => {
      setIsStopping(false);
      inputRef.current?.focus();
    }, 100);
  };

  return {refreshRequestOutcome, stopGeneration};
};

export default useStoppedRequestRecovery;
